/**
 * zip.js — the filesystem's transport.
 *
 * Neither direction holds an archive whole. A zip being read is a file on
 * disk: its directory is at the end, and each entry is a slice that is inflated
 * and handed on in pieces. A zip being written goes out in pieces as well, to
 * wherever the caller is putting it.
 *
 * The container is read by hand because the pieces have to be found before
 * they can be streamed, and the directory is where the sizes are; fflate does
 * the inflating and all of the writing.
 *
 * Archives over 4 GB (zip64) are refused on the way in, and cannot be written.
 */

import { Zip, ZipDeflate, ZipPassThrough, Inflate, strToU8 } from 'fflate';
import { normalizePath, scratch, settled } from './fs.js';

const CHUNK = 4 * 1024 * 1024;

/** Trigger a download of `data` (string, bytes or Blob) named `filename`. */
export function download(filename, data) {
  const blob = new Blob([data], { type: 'application/octet-stream' });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  // Revoked on a later turn: the click starts the fetch synchronously, but
  // revoking in the same turn has been known to race it in some browsers.
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/**
 * Write path → text, bytes or Blob entries as a zip, into `out.write`. A Blob
 * is taken to be media and stored as it is; `settled` is awaited between its
 * pieces, so they are written out before more are read in.
 */
export async function pack(entries, out, settled = async () => {}) {
  let failed = null;
  const zip = new Zip((err, chunk) => { if (err) failed = err; else out.write(chunk); });

  for (const [path, data] of entries) {
    if (data instanceof Blob && data.size) {
      const entry = new ZipPassThrough(path);
      zip.add(entry);
      for (let at = 0; at < data.size; at += CHUNK) {
        entry.push(new Uint8Array(await data.slice(at, at + CHUNK).arrayBuffer()), at + CHUNK >= data.size);
        await settled();
      }
    } else {
      const bytes = data instanceof Blob ? new Uint8Array(0) : typeof data === 'string' ? strToU8(data) : data;
      const entry = bytes.length ? new ZipDeflate(path, { level: 6 }) : new ZipPassThrough(path);
      zip.add(entry);
      entry.push(bytes, true);
    }
    if (failed) throw failed;
  }
  zip.end();
  if (failed) throw failed;
}

/** Build a zip on disk, piece by piece, and hand it over as a download. */
export async function downloadZip(filename, entries) {
  const out = scratch(filename);
  await pack(entries, out, settled);
  download(filename, await out.close());
}

const END_SIG = 0x06054b50;
const DIR_SIG = 0x02014b50;

/**
 * A zip's directory: one `{ path, isDir, method, offset, size }` per entry,
 * read from the end of the file without touching the entries. Throws if the
 * file is not a zip this can read.
 */
export async function directory(file) {
  // The end record is the last thing in the file but for a comment of up to 64 KB.
  const tail = new DataView(await file.slice(Math.max(0, file.size - 65557)).arrayBuffer());
  let end = tail.byteLength - 22;
  while (end >= 0 && tail.getUint32(end, true) !== END_SIG) end--;
  if (end < 0) throw new Error('not a zip');

  const count = tail.getUint16(end + 10, true);
  const size  = tail.getUint32(end + 12, true);
  const start = tail.getUint32(end + 16, true);
  if (count === 0xffff || size === 0xffffffff || start === 0xffffffff) throw new Error('zip64');

  const dir = new DataView(await file.slice(start, start + size).arrayBuffer());
  const entries = [];
  for (let at = 0, i = 0; i < count; i++) {
    if (dir.getUint32(at, true) !== DIR_SIG) throw new Error('bad zip directory');
    const nameLength = dir.getUint16(at + 28, true);
    const name = new TextDecoder().decode(new Uint8Array(dir.buffer, at + 46, nameLength));
    const packed = dir.getUint32(at + 20, true);
    const offset = dir.getUint32(at + 42, true);
    if (packed === 0xffffffff || offset === 0xffffffff) throw new Error('zip64');
    // Normalized so an archive built elsewhere cannot introduce `..` or an
    // absolute name.
    const path = normalizePath(name);
    if (path) {
      entries.push({ path, isDir: name.endsWith('/'), method: dir.getUint16(at + 10, true), offset, size: packed });
    }
    at += 46 + nameLength + dir.getUint16(at + 30, true) + dir.getUint16(at + 32, true);
  }
  return entries;
}

/**
 * Stream one entry's contents into `out.write`, awaiting `settled` between
 * pieces so they are written out before more are read in.
 */
export async function extract(file, entry, out, settled = async () => {}) {
  if (entry.method !== 0 && entry.method !== 8) throw new Error(`${entry.path}: unknown compression`);

  // The entry's own header repeats the name and may carry different extras
  // than the directory did, so its length is read from itself.
  const head  = new DataView(await file.slice(entry.offset, entry.offset + 30).arrayBuffer());
  const start = entry.offset + 30 + head.getUint16(26, true) + head.getUint16(28, true);

  const inflate = entry.method === 8 ? new Inflate((chunk) => out.write(chunk)) : null;
  for (let at = 0; at < entry.size; at += CHUNK) {
    const piece = new Uint8Array(await file.slice(start + at, start + Math.min(at + CHUNK, entry.size)).arrayBuffer());
    if (inflate) inflate.push(piece, at + CHUNK >= entry.size);
    else out.write(piece);
    await settled();
  }
}
